import { createSignal, createUniqueId, Show, untrack } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import {
  behaviourHintKey,
  removeSubcategory,
  type SubcategoryDraft,
  showsOfferSubcategoriesHint,
} from "../../utils/subcategory-filter";
import { CategoryAccordion } from "./category-accordion";
import { SubcategoryChips } from "./subcategory-chips";

const I18N = "company.search.freelancers.result.filters.job_subcategory";

/** The house `Select` trigger classes, for a trigger that is not a `Select`. */
const TRIGGER_CLASS =
  "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-white px-3 py-2 text-left text-sm shadow-xs ring-offset-background transition-shadow focus:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring";

export type CategorySubcategoryFieldProps = {
  label?: string;
  /** `""` = none. */
  category: string;
  subcategories: string[];
  onChange: (value: { category: string; subcategories: string[] }) => void;
  surface?: "search" | "pool";
  /** The field sits inside the «Filtri» modal sheet. */
  inSheet?: boolean;
  /** Accessor; the field never mounts a query. */
  offerContext?: () => {
    active: boolean;
    offer: { category: string; subcategories: string[] } | null;
  };
};

/**
 * The public «Categoria e ambiti» field: label, trigger that summarises the
 * choice, the popover with the accordion, the chips and the hints. Controlled:
 * the form value is the popover draft.
 */
export const CategorySubcategoryField = (
  props: CategorySubcategoryFieldProps,
) => {
  const { t } = useI18n();
  const labelId = createUniqueId();
  const valueId = createUniqueId();
  const hintId = createUniqueId();
  const [open, setOpen] = createSignal(false);
  const [api, setApi] = createSignal<{ focusInitial: () => void }>();
  let trigger: HTMLButtonElement | undefined;
  let chips: HTMLUListElement | undefined;

  const surface = () => props.surface ?? "search";
  const draft = (): SubcategoryDraft => ({
    category: props.category,
    subcategories: props.subcategories,
  });
  const count = () => props.subcategories.length;

  /** The removed chip goes with its button: focus to the chip now there, or the trigger. */
  const removeChip = (key: string, index: number) => {
    props.onChange(removeSubcategory(draft(), key).next);
    queueMicrotask(() => {
      // With the last chip gone the list goes too: the trigger keeps the focus.
      const buttons = chips?.isConnected
        ? [...chips.querySelectorAll<HTMLElement>("button")]
        : [];
      const next = buttons[Math.min(index, buttons.length - 1)];
      (next ?? trigger)?.focus();
    });
  };

  const showsOfferHint = () => {
    const ctx = props.offerContext?.();
    return (
      !!ctx &&
      showsOfferSubcategoriesHint({
        active: ctx.active,
        offer: ctx.offer,
        draft: draft(),
      })
    );
  };

  return (
    <div class="flex flex-col gap-1">
      <span id={labelId} class="text-sm font-medium">
        {props.label ?? t(`${I18N}.label`)}
      </span>
      <Popover
        open={open()}
        onOpenChange={setOpen}
        placement="bottom"
        preventScroll={props.inSheet}
      >
        <PopoverTrigger
          ref={trigger}
          class={TRIGGER_CLASS}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-describedby={props.category ? hintId : undefined}
        >
          <span id={valueId} class="flex min-w-0">
            <Show
              when={props.category}
              fallback={
                <span class="min-w-0 truncate text-muted-foreground">
                  {t(`${I18N}.all_categories`)}
                </span>
              }
            >
              <span class="min-w-0 truncate">
                {categoryLabel(props.category)}
              </span>
              <Show when={count() > 0}>
                <span class="shrink-0 whitespace-nowrap">
                  {" · "}
                  {count() === 1
                    ? t(`${I18N}.count_one`)
                    : t(`${I18N}.count`, { count: count() })}
                </span>
              </Show>
            </Show>
          </span>
          <i class="ph ph-caret-down shrink-0 opacity-50" aria-hidden="true" />
        </PopoverTrigger>
        <PopoverContent
          hideCloseButton
          topLayer={props.inSheet}
          aria-labelledby={labelId}
          // The scrolling body leaves room for the sticky footer so a category
          // brought into view is not covered; on short screens the footer
          // scrolls with the list and the room is not needed.
          class="w-(--kb-popper-anchor-width) min-w-[min(16rem,calc(100vw-2rem))] p-0 [&_.overflow-y-auto]:scroll-pb-28 [@media(max-height:31.25em)]:[&_.overflow-y-auto]:scroll-pb-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            // Kobalte runs this inside an effect that tracks signals.
            untrack(() => api()?.focusInitial());
          }}
        >
          <CategoryAccordion
            surface={surface()}
            draft={draft()}
            onDraft={(next) => props.onChange(next)}
            onClose={() => setOpen(false)}
            onReady={setApi}
          />
        </PopoverContent>
      </Popover>

      <Show when={count() > 0}>
        <SubcategoryChips
          ref={(el) => {
            chips = el;
          }}
          subcategories={props.subcategories}
          onRemove={removeChip}
        />
      </Show>
      <Show when={props.category}>
        <p id={hintId} class="text-xs text-foreground/70">
          {t(behaviourHintKey(surface(), draft()))}
        </p>
      </Show>
      <Show when={showsOfferHint()}>
        <p class="text-xs text-foreground/70">{t(`${I18N}.hint_offer`)}</p>
      </Show>
    </div>
  );
};
